import * as React from 'react'

import useFocus from '../../../Shared/hooks/useFocus'
import { READ_PASSAGE_STEP_NUMBER, whiteCheckGreenBackgroundIcon } from '../../../Shared/index'
import { BECAUSE, BUT, SO } from '../../../../constants/evidence'
import { orderedSteps } from '../../helpers/containerActionHelpers'

const stepCopyByConjunction = {
  [BECAUSE]: <p>Expand a sentence using <u>because</u> to provide a reason.</p>,
  [BUT]: <p>Expand a sentence using <u>but</u> to provide an opposing idea.</p>,
  [SO]: <p>Expand a sentence using <u>so</u> to provide a result.</p>,
}

const READ_PASSAGE_HTML = <p>Read a text and highlight sentences.</p>

interface StepProps {
  step: {
    html: React.ReactElement,
    stepNumber: number
  },
  active?: boolean,
  completed?: boolean,
  handleClick?: () => void
}

const Step = ({ active, completed, handleClick, step }: StepProps) => {
  const { html, stepNumber } = step;
  if (active) {
    return (
      <section className="step-overview-step-container">
        <button className="step-overview-step active focus-on-dark" onClick={handleClick} type="button">
          <div className="left-side-container">
            <div className={`evidence-step-number-small ${active ? 'active' : ''}`}>{stepNumber}</div>
            <span className="sr-only">This is the active step:</span>
            {html}
          </div>
          <div aria-hidden="true" className="now-tag">Now</div>
        </button>
      </section>
    )
  }

  if (completed) {
    return (
      <section className="step-overview-step-container">
        <div className="step-overview-step completed">
          <img alt={whiteCheckGreenBackgroundIcon.alt} src={whiteCheckGreenBackgroundIcon.src} />
          <span className="sr-only">Completed step:</span>
          {html}
        </div>
      </section>
    )
  }

  return (
    <section className="step-overview-step-container">
      <div className="step-overview-step">
        <div className="evidence-step-number-small">{stepNumber}</div>
        {html}
      </div>
    </section>
  )
}

const StepOverview = ({ activeStep, activities, handleClick, }) => {
  const [containerRef, setContainerFocus] = useFocus()

  React.useEffect(() => {
    setContainerFocus()
  }, [activeStep])

  const orderedPrompts = orderedSteps(activities)
  const promptSteps = orderedPrompts.map((prompt, i) => ({
    stepNumber: i + 2,
    html: stepCopyByConjunction[prompt.conjunction]
  }))

  if (activeStep === READ_PASSAGE_STEP_NUMBER) {
    return (
      <div className="step-overview">
        <h1 className="no-focus-outline" ref={containerRef} tabIndex={-1}>Here’s what you’ll do</h1>
        <Step
          active={true}
          handleClick={handleClick}
          step={{ html: READ_PASSAGE_HTML, stepNumber: READ_PASSAGE_STEP_NUMBER }}
        />
        {promptSteps.map(step => (
          <Step
            active={activeStep === step.stepNumber}
            key={step.stepNumber}
            step={step}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="step-overview">
      <h1 ref={containerRef} tabIndex={-1}>Nice! Keep going!</h1>
      <Step
        active={false}
        completed={activeStep > READ_PASSAGE_STEP_NUMBER}
        handleClick={handleClick}
        step={{ html: READ_PASSAGE_HTML, stepNumber: READ_PASSAGE_STEP_NUMBER }}
      />
      {promptSteps.map(step => (
        <Step
          active={activeStep === step.stepNumber}
          completed={activeStep > step.stepNumber}
          handleClick={handleClick}
          key={step.stepNumber}
          step={step}
        />
      ))}
    </div>
  )
}

export default StepOverview
